import React, {
  ComponentProps,
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { Box } from '../uiKit/components/Box'
import { FlexBox } from '../uiKit/components/FlexBox'

import { Card } from './Card'

const AnimatedContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'shouldStartAnimation',
})<{ shouldStartAnimation?: boolean }>`
  margin-top: -100%;
  transition: all 500ms;
  position: relative;

  ${({ shouldStartAnimation }) =>
    shouldStartAnimation &&
    css`
      margin-top: 0;
      overflow: unset;
    `}
`

export const ExpandedContentWrapper = ({
  children,
  pl = 'abs.20',
  overflow = 'hidden',
  w = '100%',
  ...rest
}: ComponentProps<typeof Box>) => {
  const [startAnimation, setStartAnimation] = useState(false)

  useEffect(() => {
    setStartAnimation(true)
  }, [])

  return (
    <Box {...{ overflow, w, pl, ...rest }}>
      <AnimatedContainer {...{ shouldStartAnimation: startAnimation }}>
        {children}
      </AnimatedContainer>
    </Box>
  )
}

type Props = {
  isExpanded?: boolean
  header: React.ReactNode
  content: React.ReactNode
  children: React.ReactNode
  // component displayed when expanded but children is null (e.g. data being loaded)
  // this is not animated
  childrenFallback?: React.ReactNode
  contentProps?: ComponentProps<typeof Box>
} & Omit<ComponentProps<typeof Card>, 'content'>

export const ExpandableCard = memo(
  forwardRef<HTMLDivElement | null, Props>(
    (
      {
        isExpanded,
        header,
        content,
        children,
        childrenFallback = null,
        contentProps,
        role,
        'aria-label': ariaLabel,
        ...rest
      },
      ref,
    ) => {
      const getContent = useCallback(() => {
        if (!isExpanded) {
          return null
        }

        if (!children) {
          return childrenFallback
        }

        return (
          <ExpandedContentWrapper
            {...{
              position: 'relative',
              pl: 'xl',
            }}>
            <FlexBox
              {...{
                w: 'xxs',
                h: '100%',
                bgc: 'blue.25',
                position: 'absolute',
                left: '-8px',
              }}
            />
            {children}
          </ExpandedContentWrapper>
        )
      }, [isExpanded, children, childrenFallback])

      return (
        <FlexBox
          {...{
            fd: 'column',
            bgc: 'white.100',
            bra: 'md',
            mt: 'md',
            mb: 'md',
            w: '100%',
          }}>
          <FlexBox
            {...{
              role,
              gap: 'md',
              ai: 'center',
              w: '100%',
              fd: 'column',
              'aria-label': ariaLabel,
            }}>
            <Card {...{ ref, padding: 'abs.0', w: '100%', ...rest }}>
              <FlexBox
                {...{
                  flex: 1,
                  ai: 'center',
                  gap: 'md',
                  jc: 'space-between',
                }}>
                {header}
              </FlexBox>
              <Box {...contentProps}>{content}</Box>
            </Card>
            {getContent()}
          </FlexBox>
        </FlexBox>
      )
    },
  ),
)
